import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faShieldAlt, 
  faAngleDoubleRight, 
  faAngleDoubleLeft 
} from '@fortawesome/free-solid-svg-icons';
import { Cookies, getCookieConsentValue } from 'react-cookie-consent';
import DisclaimerPopup, { COOKIE_CONSENT_NAME } from './DisclaimerPopup';
import packageJson from '../../../package.json';

const Footer = ({ onAcceptConsent }) => {
  const [isConsented, setIsConsented] = useState(() => {
    return typeof window !== 'undefined' ? getCookieConsentValue(COOKIE_CONSENT_NAME) === "true" : false;
  });

  // If already consented, fold to the right by default to prevent overlapping page content
  const [isFolded, setIsFolded] = useState(() => {
    return typeof window !== 'undefined' ? getCookieConsentValue(COOKIE_CONSENT_NAME) === "true" : false;
  });

  useEffect(() => {
    const handleConsentEvent = () => {
      setIsConsented(true);
      // Auto fold to the right once consent accepted from any component
      setTimeout(() => {
        setIsFolded(true);
      }, 350);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('cookieConsentAccepted', handleConsentEvent);
      return () => {
        window.removeEventListener('cookieConsentAccepted', handleConsentEvent);
      };
    }
  }, []);

  const handleConsentClick = () => {
    try {
      Cookies.set(COOKIE_CONSENT_NAME, "true", { expires: 150 });
    } catch (e) {}

    setIsConsented(true);

    if (typeof onAcceptConsent === 'function') {
      onAcceptConsent();
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('cookieConsentAccepted', {
        detail: { cookieName: COOKIE_CONSENT_NAME }
      }));
    }

    // Auto-fold the panel to the right with smooth slide animation
    setTimeout(() => {
      setIsFolded(true);
    }, 350);
  };

  return (
    <>
      {/* Floating Expand Tab (docked at bottom-right when folded) */}
      <button
        type="button"
        className={`footer-fold-tab btn btn-dark btn-sm ${isFolded ? 'tab-visible' : 'tab-hidden'}`}
        onClick={() => setIsFolded(false)}
        aria-label="Expand legal and policy footer"
        title="Expand Legal, Policies & Consent Panel"
        data-testid="footer-expand-tab"
      >
        <FontAwesomeIcon icon={faAngleDoubleLeft} className="text-warning me-1 tab-arrow-icon" />
        <span className="small text-white-50">Legal &amp; Policies</span>
        {isConsented && (
          <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-1" title="Consent Agreed" />
        )}
      </button>

      {/* Main Persistent Footer Panel (slides/folds to the right) */}
      <footer 
        className={`footer fixed-bottom py-2 bg-dark text-white border-top border-warning-subtle ${isFolded ? 'footer-folded' : 'footer-expanded'}`} 
        data-testid="app-footer"
      >
        <Container fluid className="px-3 px-md-4">
          <Row className="align-items-center gy-1">
            <Col md={5} sm={12} className="text-center text-md-start">
              <span className="small text-white-50" style={{ fontSize: "0.75rem" }}>
                &copy; {new Date().getFullYear()} HealthDash | The Cloud Dashboard &amp; Personalized Cloud Dashboard (PCD) (v{packageJson.version})
              </span>
            </Col>
            <Col md={7} sm={12} className="text-center text-md-end d-flex align-items-center justify-content-center justify-content-md-end flex-wrap gap-2">
              <a
                href="/privacy-policy-pdf-v1y2022.pdf#view=fitH"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white-50 small text-decoration-none"
                title="Privacy Policy (PDF)"
                style={{ fontSize: "0.75rem" }}
              >
                Privacy Policy (PDF)
              </a>
              <span className="text-white-50 small mx-1" aria-hidden="true">|</span>
              <a
                href="https://linardsliepins.files.wordpress.com/2021/03/confidentiality-policy-pdf-v1y2021.pdf#view=fitH"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white-50 small text-decoration-none"
                title="Cookie Policy / Confidentiality Policy / ToC (PDF)"
                style={{ fontSize: "0.75rem" }}
              >
                Cookie Policy / Confidentiality Policy / ToC
              </a>
              <DisclaimerPopup variant="footer" placement="top" onAcceptConsent={onAcceptConsent} />
              <button
                type="button"
                className={`btn btn-sm footer-consent-btn px-3 py-1 ${isConsented ? 'btn-success text-white' : 'btn-warning text-dark'}`}
                onClick={handleConsentClick}
                aria-label="I Understand and Agree"
                data-testid="footer-consent-button"
                title={isConsented ? "Consent Recorded - Click to re-confirm" : "Click to grant consent and agree to terms"}
              >
                <FontAwesomeIcon icon={isConsented ? faCheckCircle : faShieldAlt} className="me-1" />
                {isConsented ? "I Understand and Agree (Agreed)" : "I Understand and Agree"}
              </button>

              {/* Fold to Right Button */}
              <button
                type="button"
                className="btn btn-outline-warning btn-sm footer-fold-btn px-2 py-1 ms-1"
                onClick={() => setIsFolded(true)}
                aria-label="Fold footer panel to the right"
                title="Fold footer panel to the right"
                data-testid="footer-fold-btn"
              >
                <span className="small me-1 d-none d-md-inline">Fold</span>
                <FontAwesomeIcon icon={faAngleDoubleRight} className="fold-arrow-icon" />
              </button>
            </Col>
          </Row>
        </Container>
      </footer>
    </>
  );
};

export default Footer;

